import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';

import { api } from '@/api';
import { OrganizationTypes } from '@/common/constants';
import { API_URL } from '@/dtos';
import { useCurrentOrganization } from '@/modules/core/hooks';
import { useFraudReviewPermissions } from '@/modules/employee/Employee/FraudReview/hooks';

import { FraudReviewStatisticPayload } from '../FraudReviewStatistic.types';
import useFraudReviewStatisticFeatureFlag from '../hooks/useFraudReviewStatisticFeatureFlag';

const QUERY_KEY = 'GET_FRAUD_REVIEW_STATISTIC';

const useGetFraudReviewStatisticQuery = () => {
  const {
    observingOrganization: { type, id },
  } = useCurrentOrganization();

  const isFeatureEnabled = useFraudReviewStatisticFeatureFlag();
  const { canViewAlert } = useFraudReviewPermissions();

  const isSystemLevel = useMemo(() => type === OrganizationTypes.system, [type]);

  const isEnabled = useMemo(
    () => !isSystemLevel && isFeatureEnabled && canViewAlert,
    [isSystemLevel, isFeatureEnabled, canViewAlert]
  );

  const { isLoading, data, isSuccess } = useQuery(
    [QUERY_KEY, type, id],
    () => api.get(API_URL.METRICS_FRAUD_REVIEW_REQUIRED),
    {
      enabled: isEnabled,
    }
  );

  const areThresholdsConfigured = useMemo(
    () => data?.data?.employee_count !== undefined && data?.data?.employee_count !== null,
    [data?.data]
  );

  const formattedData = useMemo<FraudReviewStatisticPayload | null>(() => {
    const responseData = data?.data;

    if (responseData) {
      return {
        flaggedEmployeesCount: responseData?.employee_count || 0,
      };
    }

    return null;
  }, [data?.data]);

  const isEmpty = useMemo(() => {
    if (!isEnabled) {
      return true;
    }
    // Once the request resolves, hide the card when the observed hierarchy has no thresholds.
    if (isSuccess && !areThresholdsConfigured) {
      return true;
    }
    return false;
  }, [areThresholdsConfigured, isEnabled, isSuccess]);

  return {
    isLoading,
    isEmpty,
    isSuccess,
    data: formattedData,
  };
};

export default useGetFraudReviewStatisticQuery;
